import { useState, useMemo, useEffect, Fragment } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon, ChevronRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import { track } from '../lib/track';
import { calculateCurrentAge, formatAge } from '../lib/ageCalculation';
import { getYearFromDateString, parseDateString, formatDateString, daysBetween } from '../lib/dateUtils';

interface Child {
  id: string;
  name: string;
  date_of_birth: string | null;
  age?: number;
  camp_schedules?: CampSchedule[];
}

interface CampSchedule {
  id: string;
  camp_id: string;
  child_id?: string;
  start_date: string;
  end_date: string;
  event_year?: number | null;
  camp?: {
    id: string;
    name: string;
    description?: string;
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    slug?: string;
  };
}

// Color palette for children (matching parent calendar)
const CHILD_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
];

export default function PublicCalendarView() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  // Debug: Log token on mount
  useEffect(() => {
    console.log('PublicCalendarView mounted with token:', token);
  }, [token]);

  // Fetch calendar data from Edge Function
  const { 
    data: calendarData, 
    isLoading, 
    error
  } = useQuery<{ children: Child[]; year: number; availableYears?: number[] }>(
    ['public-calendar', token, selectedYear],
    async () => {
      if (!token) throw new Error('No token provided');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const functionUrl = `${supabaseUrl}/functions/v1/calendar-share/${token}?year=${selectedYear}`;
      
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('INVALID_TOKEN');
        }
        throw new Error(`Failed to fetch calendar: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    },
    {
      enabled: !!token,
      retry: false,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 0, // Always consider data stale, so it refetches
      cacheTime: 0, // Don't cache, always fetch fresh data
    }
  );

  const children = calendarData?.children || [];
  const isInvalidToken = error && (error as Error)?.message === 'INVALID_TOKEN';

  // Use available years from API response, or fallback to extracting from children
  const availableYears = useMemo(() => {
    if (calendarData?.availableYears && calendarData.availableYears.length > 0) {
      return calendarData.availableYears;
    }
    // Fallback: extract from current children data
    const years = new Set<number>();
    years.add(currentYear); // Always include current year
    children.forEach(child => {
      child.camp_schedules?.forEach(schedule => {
        if (schedule.event_year) {
          years.add(schedule.event_year);
        } else if (schedule.start_date) {
          const year = getYearFromDateString(schedule.start_date);
          if (year !== null) {
            years.add(year);
          }
        }
      });
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [calendarData?.availableYears, children, currentYear]);

  // Assign colors to children (must be before early return)
  const childColorMap = useMemo(() => {
    const map = new Map<string, string>();
    children.forEach((child, index) => {
      map.set(child.id, CHILD_COLORS[index % CHILD_COLORS.length]);
    });
    return map;
  }, [children]);

  // Group schedules by month (must be before early return)
  const schedulesByMonth = useMemo(() => {
    const grouped: { [month: string]: CampSchedule[] } = {};
    
    children.forEach(child => {
      child.camp_schedules?.forEach(schedule => {
        const year = getYearFromDateString(schedule.start_date);
        if (year === null) return;
        
        // Extract month from date string (YYYY-MM-DD format)
        const month = schedule.start_date.split('-')[1];
        const monthKey = `${year}-${month}`;
        
        if (!grouped[monthKey]) {
          grouped[monthKey] = [];
        }
        grouped[monthKey].push({
          ...schedule,
          child_id: child.id,
        });
      });
    });

    return grouped;
  }, [children]);

  // Calculate ages for children (must be before early return)
  const childrenWithAges = useMemo(() => {
    return children.map(child => {
      const age = child.date_of_birth 
        ? calculateCurrentAge(child.date_of_birth)
        : null;
      return {
        ...child,
        age: age ? age.years : undefined,
      };
    });
  }, [children]);

  // Track analytics when page is accessed (PostHog + dataLayer for GTM/Meta)
  useEffect(() => {
    if (calendarData && token) {
      track('calendar_public_view_accessed', {
        persona: 'parent',
        source: 'shared_link',
        share_token: token.substring(0, 8) + '...', // Truncated for privacy
        year: selectedYear,
      });
    }
  }, [calendarData, token, selectedYear]);

  // Handle invalid token (after all hooks)
  if (isInvalidToken || (!isLoading && !calendarData && error)) {
    return (
      <>
        <SEO
          title="Calendar Not Found | CampWhiz"
          description="The shared calendar link is invalid or has been revoked."
          url={`/calendar/share/${token}`}
          noindex={true}
        />
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="mb-6">
              <span className="text-6xl mb-4 block">🔒</span>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Calendar Not Available</h1>
              <p className="text-gray-600">
                This calendar link is invalid or has been revoked by the owner.
              </p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="btn btn-primary"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <SEO
          title="Loading Calendar | CampWhiz"
          description="Loading shared calendar..."
          url={`/calendar/share/${token}`}
          noindex={true}
        />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={`Shared Calendar ${selectedYear} | CampWhiz`}
        description={`View shared camp schedule for ${selectedYear}`}
        url={`/calendar/share/${token}`}
        noindex={true}
      />
      <div className="page-container" style={{ minHeight: 'auto' }}>
        <div className="page-content pb-12">
          {/* Header */}
          <div className="mb-4 sm:mb-8">
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-gray-900">
              <span className="inline-flex items-center gap-2">
                {availableYears.length > 0 && (
                  <Listbox value={selectedYear} onChange={setSelectedYear}>
                    <div className="relative">
                      <Listbox.Button className="inline-flex items-center gap-1.5 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-primary hover:text-primary/80 transition-colors focus:outline-none focus:ring-0">
                        <span>{selectedYear}</span>
                        <ChevronDownIcon className="h-6 w-6 text-primary" />
                      </Listbox.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="opacity-0 scale-95"
                      enterTo="opacity-100 scale-100"
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute right-0 mt-2 w-32 max-h-60 overflow-auto rounded-lg bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                        {availableYears.map((year) => (
                          <Listbox.Option
                            key={year}
                            value={year}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 ${
                                active ? 'bg-primary/10 text-primary' : 'text-gray-900'
                              } ${selectedYear === year ? 'bg-primary/20 font-medium' : ''}`
                            }
                          >
                            {year}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
                  )}
                  Shared Camp Calendar
                </span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">Read-only view</p>
          </div>

          {/* Main Content */}
          <div>
            {childrenWithAges.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No camp schedules found for {selectedYear}.</p>
              </div>
            ) : (
              <>
                {/* Children Legend */}
                <div className="mb-4 sm:mb-8 bg-white rounded-lg shadow p-4 sm:p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Children</h2>
                <div className="flex flex-wrap gap-4">
                  {childrenWithAges.map(child => (
                    <div key={child.id} className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: childColorMap.get(child.id) }}
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {child.name}
                        {child.age !== undefined && (
                          <span className="text-gray-500 ml-1">({formatAge(child.age)})</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

                {/* Calendar Schedule List */}
                <div className="space-y-4 sm:space-y-8">
                  {Object.keys(schedulesByMonth)
                    .sort()
                    .map(monthKey => {
                      const [year, month] = monthKey.split('-');
                      const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                      const schedules = schedulesByMonth[monthKey];

                      return (
                        <div key={monthKey} className="bg-white rounded-lg shadow overflow-hidden">
                          <div className="px-4 sm:px-6 lg:px-10 py-3 sm:py-4 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-900">{monthName}</h3>
                          </div>
                          <div className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6">
                          <div className="space-y-3">
                            {[...schedules].sort((a, b) => a.start_date.localeCompare(b.start_date)).map(schedule => {
                              const child = childrenWithAges.find(c => c.id === schedule.child_id);
                              const color = childColorMap.get(schedule.child_id || '');
                              // Parse dates only for display/calculation
                              const startDate = parseDateString(schedule.start_date);
                              const endDate = parseDateString(schedule.end_date);
                              const days = startDate && endDate ? Math.abs(daysBetween(schedule.end_date, schedule.start_date)) + 1 : 1;

                              return (
                                <div
                                  key={schedule.id}
                                  className="flex items-center gap-4 p-3 sm:p-4 rounded-lg border-l-4"
                                  style={{ borderLeftColor: color }}
                                >
                                  <div
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: color }}
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      {schedule.camp ? (
                                        <Link
                                          to={
                                            schedule.camp.slug
                                              ? `/camps/${schedule.camp.slug}`
                                              : schedule.camp.state && schedule.camp.city && schedule.camp.name
                                              ? `/camps/${schedule.camp.state.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').trim()}/${schedule.camp.city.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').trim()}/${schedule.camp.name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').trim()}`
                                              : '#'
                                          }
                                          className="font-semibold text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1 group"
                                        >
                                          <span>{schedule.camp.name}</span>
                                          <ChevronRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                        </Link>
                                      ) : (
                                        <span className="font-semibold text-gray-900">Unknown Camp</span>
                                      )}
                                      {child && (
                                        <span className="text-sm text-gray-500">• {child.name}</span>
                                      )}
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">
                                      {startDate && formatDateString(schedule.start_date, {
                                        month: 'short',
                                        day: 'numeric',
                                      })}
                                      {startDate && endDate && schedule.start_date !== schedule.end_date && (
                                        <>
                                          {' - '}
                                          {formatDateString(schedule.end_date, {
                                            month: 'short',
                                            day: 'numeric',
                                            year: getYearFromDateString(schedule.start_date) !== getYearFromDateString(schedule.end_date) ? 'numeric' : undefined,
                                          })}
                                        </>
                                      )}
                                      {days > 1 && (
                                        <span className="text-gray-500 ml-2">({days} days)</span>
                                      )}
                                    </div>
                                    {schedule.camp && (schedule.camp.address || (schedule.camp.city && schedule.camp.state)) && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        <a
                                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([schedule.camp.address, schedule.camp.city, schedule.camp.state, schedule.camp.zip_code].filter(Boolean).join(', '))}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1 group no-underline"
                                        >
                                          <span>
                                            {schedule.camp.address 
                                              ? `${schedule.camp.address}${schedule.camp.city ? `, ${schedule.camp.city}` : ''}${schedule.camp.state ? `, ${schedule.camp.state}` : ''}${schedule.camp.zip_code ? ` ${schedule.camp.zip_code}` : ''}`
                                              : schedule.camp.city && schedule.camp.state
                                                ? `${schedule.camp.city}, ${schedule.camp.state}`
                                                : schedule.camp.address || ''
                                            }
                                          </span>
                                          <ArrowTopRightOnSquareIcon className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

